import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, TimePicker, Typography, Space, Progress, Input } from 'antd';
import { ExclamationCircleOutlined, CheckOutlined, ClockCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAttendance } from '@/context/AttendanceContext';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const AUTO_LOGOUT_SECONDS = 600; // 10 minutes
const MIN_OVERTIME_MINUTES = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === 'qaerp.dymaxtech.com') ? 1 : 30;

const ShiftManagementApp = ({ setOverTime, markTimeOut, timeIn }) => {
    const { attendance, refreshSession, endOvertime, elapsedMs: contextElapsedMs, requiredProductiveMs: contextRequiredMs } = useAttendance();
    const currentAdmin = useSelector(selectCurrentAdmin);

    // ─── State ─────────────────────────────────────────────
    const [modalVisible, setModalVisible] = useState(false);
    const [preWarningVisible, setPreWarningVisible] = useState(false);
    const [overtimeEndModalVisible, setOvertimeEndModalVisible] = useState(false);
    const [countdown, setCountdown] = useState(AUTO_LOGOUT_SECONDS);
    const [overtimeCountdown, setOvertimeCountdown] = useState(AUTO_LOGOUT_SECONDS);
    const [showOvertimeForm, setShowOvertimeForm] = useState(false);
    const [overtimeEndTime, setOvertimeEndTime] = useState(null);
    const [validationError, setValidationError] = useState('');
    const [liveOvertimeClock, setLiveOvertimeClock] = useState('00:00:00');
    const [overTimeRemarks, setOverTimeRemarks] = useState('');
    const [isProcessingOvertime, setIsProcessingOvertime] = useState(false);
    const [liveProductiveMs, setLiveProductiveMs] = useState(0);

    const audioRef = useRef(null);
    const intervalRef = useRef(null);
    // Use a ref so handleEndShift always has access to the latest overtime clock
    const liveOvertimeClockRef = useRef('00:00:00');
    // Track if we already triggered the modals for this "productive threshold crossing"
    const shiftEndTriggeredRef = useRef(false);
    const preWarningTriggeredRef = useRef(false);
    const lastAttendanceIdRef = useRef(null);

    // ─── Derived Data ──────────────────────────────────────
    const activeOvertimeSession = attendance?.overtimeSessions?.find(s => !s.actualEndTime);
    const overtimeHistory = attendance?.overtimeSessions?.filter(s => s.actualEndTime) || [];

    // Calculate total marked-in time (Clock-In Time minus interruptions like breaks)
    const getMarkedInTimeMs = useCallback(() => {
        if (!timeIn) return 0;
        const totalClockMs = Date.now() - new Date(timeIn).getTime();
        // Subtract break time because the shift limit usually refers to "productive" presence
        return totalClockMs - (attendance?.totalBreakMs || 0);
    }, [timeIn, attendance?.totalBreakMs]);

    const markedInTimeMs = getMarkedInTimeMs();

    const getRequiredProductiveMs = useCallback(() => {
        // Primary: Use currentAdmin.workSchedule (already accounts for temporary overrides from auth)
        console.log("[ShiftEndModal] Calculating required productive ms...", currentAdmin);
        if (currentAdmin?.workSchedule) {
            const todayKey = dayjs().format('ddd');
            const todaySchedule = currentAdmin.workSchedule[todayKey];
            if (todaySchedule?.productiveHours) {
                const [hours, minutes] = todaySchedule.productiveHours.split(':').map(Number);
                return ((hours * 60 * 60) + (minutes * 60)) * 1000;
            }
        }
        // Fallback: Use backend-calculated value from active session context
        return contextRequiredMs || attendance?.requiredProductiveMs || 27900000;
    }, [currentAdmin, attendance?.requiredProductiveMs, contextRequiredMs]);

    const requiredProductiveMs = getRequiredProductiveMs();

    // ─── Live Productive Ms (Synchronized with Context) ─────
    useEffect(() => {
        // We sync the local state with the context's absolute time.
        // Since the context now uses absolute wall-clock diffing, 
        // this value will be accurate even if the tab was in the background.
        setLiveProductiveMs(contextElapsedMs || 0);
    }, [contextElapsedMs]);

    // ─── Audio Helpers ──────────────────────────────────────
    const playNotificationSound = useCallback((durationMs = 600000) => {
        stopNotificationSound(); // clean up any previous
        audioRef.current = new Audio("/mixkit-happy-bells-notification-937.wav");
        audioRef.current.loop = false;
        const startTime = Date.now();

        const playOnce = () => {
            if (!audioRef.current) return;
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => { });
            if (Date.now() - startTime > durationMs) stopNotificationSound();
        };

        playOnce();
        intervalRef.current = setInterval(playOnce, 3000);
    }, []);

    const stopNotificationSound = useCallback(() => {
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; audioRef.current = null; }
    }, []);

    // cleanup on unmount
    useEffect(() => () => stopNotificationSound(), []);

    // ─── Overtime Clock ────────────────────────────────────
    useEffect(() => {
        if (!activeOvertimeSession && overtimeHistory.length === 0) {
            setLiveOvertimeClock('00:00:00');
            liveOvertimeClockRef.current = '00:00:00';
            return;
        }

        const tick = () => {
            let totalSeconds = 0;
            (attendance?.overtimeSessions || []).forEach(session => {
                if (session.startTime) {
                    const start = dayjs(session.startTime);
                    const end = session.actualEndTime ? dayjs(session.actualEndTime) : dayjs();
                    totalSeconds += end.diff(start, 'seconds');
                }
            });

            const hh = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
            const mm = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
            const ss = (totalSeconds % 60).toString().padStart(2, '0');
            const formatted = `${hh}:${mm}:${ss}`;
            setLiveOvertimeClock(formatted);
            liveOvertimeClockRef.current = formatted;
        };

        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [activeOvertimeSession, overtimeHistory.length, attendance]);

    // ─── Trigger: Shift Modals ───────────────────────────────
    useEffect(() => {
        // We only need currentAdmin for the workSchedule, but we can trigger without attendance._id if needed
        if (!currentAdmin) return;

        // Reset refs if the attendance session changes (e.g., new shift or refresh)
        const currentSessionId = attendance?._id || 'local-session';
        if (currentSessionId !== lastAttendanceIdRef.current) {
            console.log(`[ShiftEndModal] Session changed from ${lastAttendanceIdRef.current} to ${currentSessionId}. Resetting triggers.`);
            lastAttendanceIdRef.current = currentSessionId;
            shiftEndTriggeredRef.current = false;
            preWarningTriggeredRef.current = false;
        }

        const thresholdMs = 900000; // 15 minutes

        // Suppression Logic
        const isClosing = attendance?.isClosed || isProcessingOvertime;

        // Always log state to console for debugging (throttle to 10s)
        const now = Date.now();
        if (!window._lastLogTime || now - window._lastLogTime > 10000) {
            window._lastLogTime = now;
            const trackingType = currentAdmin?.trackingType || 'productivity';
            const currentTimeUsed = trackingType === 'management' ? markedInTimeMs : liveProductiveMs;
            console.log(`[ShiftEndModal Status] Tracking: ${trackingType}, Used: ${Math.floor(currentTimeUsed / 60000)}m, Req: ${Math.floor(requiredProductiveMs / 60000)}m, Diff: ${Math.floor((requiredProductiveMs - currentTimeUsed) / 60000)}m`);
            console.log(`[ShiftEndModal Flags] modalVisible: ${modalVisible}, preWarningVisible: ${preWarningVisible}, shiftEndTriggered: ${shiftEndTriggeredRef.current}, OTActive: ${!!activeOvertimeSession}`);
        }

        // 1. Suppression: If OT is active or session is closing, don't show shift-end/pre-warning popups
        if (activeOvertimeSession) return;
        if (isClosing) return;

        // Determine if shift end condition is met
        const isAtEnd = liveProductiveMs >= requiredProductiveMs;
        const isNearEnd = liveProductiveMs >= (requiredProductiveMs - thresholdMs);

        // 2. Pre-Warning Trigger (15 mins before end)
        if (
            requiredProductiveMs > thresholdMs &&
            isNearEnd &&
            !isAtEnd &&
            !preWarningVisible &&
            !modalVisible &&
            !overtimeEndModalVisible &&
            !preWarningTriggeredRef.current
        ) {
            console.warn('[ShiftEndModal] TRIGGER: Pre-Warning Threshold Reached');
            preWarningTriggeredRef.current = true;
            playNotificationSound(300000);
            setPreWarningVisible(true);
        }

        // 3. Final Shift End Trigger
        if (
            isAtEnd &&
            !modalVisible &&
            !overtimeEndModalVisible &&
            !shiftEndTriggeredRef.current
        ) {
            console.warn('[ShiftEndModal] TRIGGER: Final Shift End Reached');
            shiftEndTriggeredRef.current = true;
            setPreWarningVisible(false); // Close pre-warning if it was open
            playNotificationSound();
            setModalVisible(true);
        }
    }, [
        liveProductiveMs,
        markedInTimeMs,
        requiredProductiveMs,
        modalVisible,
        preWarningVisible,
        overtimeEndModalVisible,
        activeOvertimeSession,
        isProcessingOvertime,
        attendance?.isClosed,
        attendance?._id,
        currentAdmin,
        timeIn
    ]);

    // ─── Trigger: Overtime End Modal ────────────────────────
    useEffect(() => {
        if (!activeOvertimeSession?.plannedEndTime) return;

        const id = setInterval(() => {
            const now = dayjs();
            const planned = dayjs(activeOvertimeSession.plannedEndTime);
            if (planned.isBefore(now) && !overtimeEndModalVisible && !modalVisible) {
                playNotificationSound();
                setOvertimeEndModalVisible(true);
            }
        }, 1000);

        return () => clearInterval(id);

    }, [activeOvertimeSession, overtimeEndModalVisible, modalVisible]);

    // ─── Countdown: Shift End / Pre-Warning ─────────────────
    useEffect(() => {
        if (!modalVisible) return; // Decoupled from pre-warning
        setCountdown(AUTO_LOGOUT_SECONDS);
        setShowOvertimeForm(false);
        setOvertimeEndTime(null);
        setValidationError('');
    }, [modalVisible]);

    useEffect(() => {
        if (!modalVisible || countdown <= 0) return; // Decoupled from pre-warning
        const id = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    // auto mark-out
                    clearInterval(id);
                    doEndShift();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [modalVisible, countdown > 0]); // only re-create when modal opens/closes

    // ─── Countdown: Overtime End ─────────────────────────────
    useEffect(() => {
        if (!overtimeEndModalVisible) return;
        setOvertimeCountdown(AUTO_LOGOUT_SECONDS);
        setShowOvertimeForm(false);
        setOvertimeEndTime(null);
        setValidationError('');
    }, [overtimeEndModalVisible]);

    useEffect(() => {
        if (!overtimeEndModalVisible || overtimeCountdown <= 0) return;
        const id = setInterval(() => {
            setOvertimeCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(id);
                    doEndShift();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [overtimeEndModalVisible, overtimeCountdown > 0]);

    // ─── Actions ──────────────────────────────────────────
    const doEndShift = useCallback(() => {
        stopNotificationSound();
        const ot = liveOvertimeClockRef.current || '00:00:00';
        setOverTime(ot);
        setModalVisible(false);
        setPreWarningVisible(false);
        setOvertimeEndModalVisible(false);
        markTimeOut(ot, overTimeRemarks);
        setOverTimeRemarks('');
    }, [markTimeOut, setOverTime, stopNotificationSound, overTimeRemarks]);

    const handleEndShift = () => {
        doEndShift();
    };

    const handleStartOvertime = async () => {
        if (!showOvertimeForm) {
            setShowOvertimeForm(true);
            setOvertimeEndTime(dayjs().add(MIN_OVERTIME_MINUTES, 'minutes'));
            stopNotificationSound();
            return;
        }


        if (!overtimeEndTime) {
            setValidationError('Please select overtime end time!');
            return;
        }

        const formattedTime12 = overtimeEndTime.format('h:mm A');
        const now = dayjs();
        let finalOvertimeTime = dayjs().hour(overtimeEndTime.hour()).minute(overtimeEndTime.minute()).second(0);
        if (finalOvertimeTime.isBefore(now)) {
            finalOvertimeTime = finalOvertimeTime.add(1, 'day');
        }

        if (finalOvertimeTime.diff(now, 'minutes') < MIN_OVERTIME_MINUTES) {
            setValidationError(`Overtime end time must be at least ${MIN_OVERTIME_MINUTES} minute${MIN_OVERTIME_MINUTES === 1 ? '' : 's'} in the future!`);
            return;
        }

        setValidationError('');
        setIsProcessingOvertime(true);

        try {
            await axios.post('/attendance/overtime/start', {
                plannedEndTime: finalOvertimeTime.toISOString(),
                remarks: overTimeRemarks
            });

            if (refreshSession) await refreshSession();

            setOverTime(formattedTime12);
            setModalVisible(false);
            setPreWarningVisible(false);
            stopNotificationSound();
            setOvertimeEndTime(null);
            setOverTimeRemarks('');
            // Allow the OT-end trigger to re-fire when this new session expires
            shiftEndTriggeredRef.current = true;
            preWarningTriggeredRef.current = true;
        } catch (error) {
            console.error("Failed to start overtime", error);
        } finally {
            setIsProcessingOvertime(false);
        }
    };

    const handleOverTimeSlackNot = (overtimeEndTimeStr) => {
        try {
            const end = dayjs(overtimeEndTimeStr, ["h:mm A"]);
            const now = dayjs();
            const diffMinutes = end.isAfter(now) ? end.diff(now, 'minute') : end.add(1, 'day').diff(now, 'minute');
            const diffHours = (diffMinutes / 60).toFixed(2);

            axios.post("/slackOverTimeAlert", {
                name: `${currentAdmin.name} ${currentAdmin.surname}`,
                overtime: Number(diffHours),
            });
        } catch (error) {
            console.error("error", error?.message);
        }
    };

    const handleExtendOvertime = async () => {
        if (!showOvertimeForm) {
            setShowOvertimeForm(true);
            setOvertimeEndTime(dayjs().add(MIN_OVERTIME_MINUTES, 'minutes'));
            stopNotificationSound();
            return;
        }

        if (!overtimeEndTime) {
            setValidationError('Please select overtime end time!');
            return;
        }

        const formattedTime12 = overtimeEndTime.format('h:mm A');
        const now = dayjs();
        let finalOvertimeTime = dayjs().hour(overtimeEndTime.hour()).minute(overtimeEndTime.minute()).second(0);
        if (finalOvertimeTime.isBefore(now)) {
            finalOvertimeTime = finalOvertimeTime.add(1, 'day');
        }

        if (finalOvertimeTime.diff(now, 'minutes') < MIN_OVERTIME_MINUTES) {
            setValidationError(`Overtime end time must be at least ${MIN_OVERTIME_MINUTES} minute${MIN_OVERTIME_MINUTES === 1 ? '' : 's'} in the future!`);
            return;
        }

        setValidationError('');
        setIsProcessingOvertime(true);

        try {
            // Close the old OT session on the backend
            if (endOvertime) await endOvertime();

            await axios.post('/attendance/overtime/start', {
                plannedEndTime: finalOvertimeTime.toISOString(),
                remarks: overTimeRemarks
            });

            if (refreshSession) await refreshSession();

            setOverTime(formattedTime12);
            setOvertimeEndModalVisible(false);
            stopNotificationSound();
            setOvertimeEndTime(null);
            setOverTimeRemarks('');
            shiftEndTriggeredRef.current = true;
            preWarningTriggeredRef.current = true;
        } catch (e) {
            console.error("Failed to end/extend overtime session:", e);
        } finally {
            setIsProcessingOvertime(false);
        }
    };

    const handleCancel = () => {
        setShowOvertimeForm(false);
        setOvertimeEndTime(null);
        setValidationError('');
    };

    // ─── Helpers ────────────────────────────────────────────
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progressPercent = ((AUTO_LOGOUT_SECONDS - countdown) / AUTO_LOGOUT_SECONDS) * 100;
    const overtimeProgressPercent = ((AUTO_LOGOUT_SECONDS - overtimeCountdown) / AUTO_LOGOUT_SECONDS) * 100;

    const getCurrentTime = () => dayjs().format('h:mm:ss A');

    const getScheduledEndTimeStr = () => {
        if (!currentAdmin?.workSchedule) return 'N/A';
        const todayKey = dayjs().format('ddd');
        const todaySchedule = currentAdmin.workSchedule[todayKey];
        if (!todaySchedule?.endTime) return 'N/A';
        return todaySchedule.endTime; // e.g., "05:00 PM"
    };

    return (
        <>
            {/* ── Pre-Shift Warning Modal ────────────────────── */}
            <Modal
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                        <span>⚠️ Shift Ending Soon</span>
                    </Space>
                }
                open={preWarningVisible}
                onCancel={() => { }}
                footer={null}
                width={400}
                centered
                maskClosable={false}
                closable={false}
                className="mini-warning-modal"
            >
                <div style={{ padding: '8px 0' }}>
                    <Paragraph style={{ fontSize: '15px', marginBottom: '24px', textAlign: 'center' }}>
                        This is a reminder that you have <strong>15 minutes remaining</strong> to complete your scheduled productive hours for today.
                    </Paragraph>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => {
                                setPreWarningVisible(false);
                                stopNotificationSound();
                            }}
                            style={{ background: '#1890ff', borderColor: '#1890ff' }}
                        >
                            OK, I Understand
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* ── Shift End Modal ─────────────────────────────── */}
            < Modal
                title={
                    < Space >
                        <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                        <span>⚠️ Shift Ended – Choose Next Action</span>
                    </Space >
                }
                open={modalVisible}
                onCancel={() => { }}
                footer={null}
                width={500}
                centered
                maskClosable={false}
                closable={false}
            >
                <div style={{ padding: '16px 0' }}>
                    {/* User Info Section */}
                    <div className="shift-modal-section user-info-section">
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong>Employee: {`${currentAdmin.name} ${currentAdmin.surname}` || 'Unknown'}</Text>
                            <Text type="secondary">
                                Scheduled End: {getScheduledEndTimeStr()}
                            </Text>
                            <Text type="secondary">
                                Current Time: {getCurrentTime()}
                            </Text>
                            {(overtimeHistory.length > 0 || activeOvertimeSession) && (
                                <div className="total-overtime-badge">
                                    <Text strong className="total-overtime-text">
                                        ⏱️ Total Overtime: {liveOvertimeClock}
                                    </Text>
                                </div>
                            )}
                        </Space>
                    </div>

                    {/* Main Message */}
                    <Paragraph style={{ fontSize: '16px', marginBottom: '20px' }}>
                        Your scheduled shift has ended. Please select one of the following options to proceed:
                    </Paragraph>

                    <div style={{ marginBottom: '20px' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>• <strong>End Shift</strong> – Marks your time-out and logs you out of the system.</Text>
                            <Text>• <strong>{overtimeHistory.length > 0 ? 'Extend Overtime' : 'Start Overtime'}</strong> – Continue working beyond your scheduled shift. You'll be asked to enter your overtime end time, and the system will remind you before your overtime ends.</Text>
                        </Space>
                    </div>

                    {/* Overtime Form */}
                    {showOvertimeForm && (
                        <div className="shift-modal-section overtime-form-section">
                            <Title level={5} style={{ marginBottom: '12px' }}>
                                <ClockCircleOutlined /> Set Overtime End Time
                                {overtimeHistory.length > 0 && (
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal', marginLeft: '8px' }}>
                                        (Session #{overtimeHistory.length + (activeOvertimeSession ? 1 : 0) + 1})
                                    </Text>
                                )}
                            </Title>
                            <div style={{ marginBottom: '12px' }}>
                                <Text style={{ marginBottom: '8px', display: 'block' }}>
                                    Expected Overtime End Time *
                                </Text>
                                <TimePicker
                                    value={overtimeEndTime}
                                    onChange={(time) => {
                                        setOvertimeEndTime(time);
                                        setValidationError('');
                                    }}
                                    format="h:mm A"
                                    use12Hours
                                    placeholder="Select end time"
                                    style={{ width: '100%' }}
                                    disabledHours={() => {
                                        const now = dayjs();
                                        const disabled = [];
                                        for (let h = 0; h < 24; h++) {
                                            let allMinutesInvalid = true;
                                            for (let m = 0; m < 60; m++) {
                                                let t = dayjs().hour(h).minute(m).second(0);
                                                if (t.isBefore(now)) t = t.add(1, 'day');
                                                if (t.diff(now, 'minutes') >= MIN_OVERTIME_MINUTES) {
                                                    allMinutesInvalid = false;
                                                    break;
                                                }
                                            }
                                            if (allMinutesInvalid) disabled.push(h);
                                        }
                                        return disabled;
                                    }}
                                    disabledMinutes={(selectedHour) => {
                                        const now = dayjs();
                                        const disabled = [];
                                        for (let m = 0; m < 60; m++) {
                                            let t = dayjs().hour(selectedHour).minute(m).second(0);
                                            if (t.isBefore(now)) t = t.add(1, 'day');
                                            if (t.diff(now, 'minutes') < MIN_OVERTIME_MINUTES) disabled.push(m);
                                        }
                                        return disabled;
                                    }}
                                    showNow={false}
                                />
                                {validationError && (
                                    <Text type="danger" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                        {validationError}
                                    </Text>
                                )}
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <Text style={{ marginBottom: '8px', display: 'block' }}>
                                    Overtime Remarks (What will you be working on?)
                                </Text>
                                <TextArea
                                    rows={3}
                                    placeholder="Briefly describe the tasks you'll perform during overtime..."
                                    value={overTimeRemarks}
                                    onChange={(e) => setOverTimeRemarks(e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Auto-logout Warning */}
                    <div className="shift-modal-section auto-logout-section">
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong style={{ color: '#fa541c' }}>
                                ⏰ Auto-logout Warning
                            </Text>
                            <Text>
                                If no action is taken, you will be automatically logged out in{' '}
                                <strong style={{ color: '#fa541c' }}>{formatTime(countdown)}</strong>
                            </Text>
                            <Progress
                                percent={progressPercent}
                                size="small"
                                strokeColor="#fa541c"
                                showInfo={false}
                            />
                        </Space>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={<CheckOutlined />}
                            onClick={handleEndShift}
                            style={{
                                background: '#52c41a',
                                borderColor: '#52c41a',
                                minWidth: '140px'
                            }}
                        >
                            ✅ End Shift
                        </Button>

                        <Button
                            type="primary"
                            size="large"
                            icon={<ClockCircleOutlined />}
                            onClick={handleStartOvertime}
                            loading={isProcessingOvertime}
                            style={{
                                background: '#1890ff',
                                borderColor: '#1890ff',
                                minWidth: '140px'
                            }}
                        >
                            ⏱️ {showOvertimeForm ? (overtimeHistory.length > 0 ? 'Confirm Extension' : 'Confirm Overtime') : (overtimeHistory.length > 0 ? 'Extend Overtime' : 'Start Overtime')}
                        </Button>

                        {showOvertimeForm && (
                            <Button
                                size="large"
                                icon={<CloseOutlined />}
                                onClick={handleCancel}
                                style={{ minWidth: '100px' }}
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </div>
            </Modal >

            {/* ── Overtime End Modal ──────────────────────────── */}
            < Modal
                title={
                    < Space >
                        <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                        <span>⚠️ Overtime Ended – Choose Next Action</span>
                    </Space >
                }
                open={overtimeEndModalVisible}
                footer={null}
                width={500}
                centered
                maskClosable={false}
                closable={false}
            >
                <div style={{ padding: '16px 0' }}>
                    {/* User Info Section */}
                    <div style={{
                        background: '#f5f5f5',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '20px'
                    }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong>Employee: {`${currentAdmin.name} ${currentAdmin.surname}` || 'Unknown'}</Text>
                            <Text type="secondary">
                                Overtime End Time: {activeOvertimeSession ? dayjs(activeOvertimeSession.plannedEndTime).format('h:mm A') : 'N/A'}
                            </Text>
                            <Text type="secondary">
                                Current Time: {getCurrentTime()}
                            </Text>
                            <div style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                background: '#e6f7ff',
                                borderRadius: '4px',
                                border: '1px solid #91d5ff'
                            }}>
                                <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                                    ⏱️ Total Overtime: {liveOvertimeClock}
                                </Text>
                            </div>
                            {activeOvertimeSession && (
                                <Text type="secondary">
                                    Current Session: #{overtimeHistory.length + 1}
                                </Text>
                            )}
                        </Space>
                    </div>

                    {/* Main Message */}
                    <Paragraph style={{ fontSize: '16px', marginBottom: '20px' }}>
                        Your overtime has ended. Please select one of the following options to proceed:
                    </Paragraph>

                    <div style={{ marginBottom: '20px' }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>• <strong>End Shift</strong> – Marks your time-out and logs you out of the system.</Text>
                            <Text>• <strong>Extend Overtime</strong> – Continue working beyond your current overtime. You'll be asked to enter a new overtime end time.</Text>
                        </Space>
                    </div>

                    {/* Overtime Form */}
                    {showOvertimeForm && (
                        <div className="shift-modal-section overtime-form-section" style={{ marginBottom: '20px' }}>
                            <Title level={5} style={{ marginBottom: '12px' }}>
                                <ClockCircleOutlined /> Set Overtime End Time
                                {overtimeHistory.length > 0 && (
                                    <Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal', marginLeft: '8px' }}>
                                        (Session #{overtimeHistory.length + (activeOvertimeSession ? 1 : 0) + 1})
                                    </Text>
                                )}
                            </Title>
                            <div style={{ marginBottom: '12px' }}>
                                <Text style={{ marginBottom: '8px', display: 'block' }}>
                                    Expected Overtime End Time *
                                </Text>
                                <TimePicker
                                    value={overtimeEndTime}
                                    onChange={(time) => {
                                        setOvertimeEndTime(time);
                                        setValidationError('');
                                    }}
                                    format="h:mm A"
                                    use12Hours
                                    placeholder="Select end time"
                                    style={{ width: '100%' }}
                                    disabledHours={() => {
                                        const now = dayjs();
                                        const disabled = [];
                                        for (let h = 0; h < 24; h++) {
                                            let allMinutesInvalid = true;
                                            for (let m = 0; m < 60; m++) {
                                                let t = dayjs().hour(h).minute(m).second(0);
                                                if (t.isBefore(now)) t = t.add(1, 'day');
                                                if (t.diff(now, 'minutes') >= MIN_OVERTIME_MINUTES) {
                                                    allMinutesInvalid = false;
                                                    break;
                                                }
                                            }
                                            if (allMinutesInvalid) disabled.push(h);
                                        }
                                        return disabled;
                                    }}
                                    disabledMinutes={(selectedHour) => {
                                        const now = dayjs();
                                        const disabled = [];
                                        for (let m = 0; m < 60; m++) {
                                            let t = dayjs().hour(selectedHour).minute(m).second(0);
                                            if (t.isBefore(now)) t = t.add(1, 'day');
                                            if (t.diff(now, 'minutes') < MIN_OVERTIME_MINUTES) disabled.push(m);
                                        }
                                        return disabled;
                                    }}
                                    showNow={false}
                                />
                                {validationError && (
                                    <Text type="danger" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                        {validationError}
                                    </Text>
                                )}
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <Text style={{ marginBottom: '8px', display: 'block' }}>
                                    Overtime Remarks (What will you be working on?)
                                </Text>
                                <TextArea
                                    rows={3}
                                    placeholder="Briefly describe the tasks you'll perform during overtime..."
                                    value={overTimeRemarks}
                                    onChange={(e) => setOverTimeRemarks(e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Auto-logout Warning for Overtime End */}
                    <div style={{
                        background: '#fff2e8',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '20px',
                        border: '1px solid #ffbb96'
                    }}>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong style={{ color: '#fa541c' }}>
                                ⏰ Auto-logout Warning
                            </Text>
                            <Text>
                                If no action is taken, you will be automatically logged out in{' '}
                                <strong style={{ color: '#fa541c' }}>{formatTime(overtimeCountdown)}</strong>
                            </Text>
                            <Progress
                                percent={overtimeProgressPercent}
                                size="small"
                                strokeColor="#fa541c"
                                showInfo={false}
                            />
                        </Space>
                    </div>

                    {/* Action Buttons for Overtime End */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={<CheckOutlined />}
                            onClick={handleEndShift}
                            style={{
                                background: '#52c41a',
                                borderColor: '#52c41a',
                                minWidth: '140px'
                            }}
                        >
                            ✅ End Shift
                        </Button>

                        <Button
                            type="primary"
                            size="large"
                            icon={<ClockCircleOutlined />}
                            onClick={handleExtendOvertime}
                            loading={isProcessingOvertime}
                            style={{
                                background: '#1890ff',
                                borderColor: '#1890ff',
                                minWidth: '140px'
                            }}
                        >
                            ⏱️ {showOvertimeForm ? 'Confirm Extension' : 'Extend Overtime'}
                        </Button>

                        {showOvertimeForm && (
                            <Button
                                size="large"
                                icon={<CloseOutlined />}
                                onClick={handleCancel}
                                style={{ minWidth: '100px' }}
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </div>
            </Modal >
        </>
    );
};

export default ShiftManagementApp;
